import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useSubscription } from "@apollo/client";
import { useQueryClient } from "@tanstack/react-query";
import { isSameDay, isToday, isYesterday } from "date-fns";
import { formatShortDate } from "@/lib/dates";
import {
  ArrowDown,
  ArrowLeft,
  BellOff,
  Flag,
  Loader2,
  MoreHorizontal,
  Search,
  Shield,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import ReportUserDialog from "@/components/ReportUserDialog";
import {
  BLOCK_USER,
  CHAT_BLOCKED_USERS,
  CONVERSATION_MESSAGES,
  UNBLOCK_USER,
  MARK_CONVERSATION_READ,
  MESSAGE_ADDED,
  MESSAGE_UPDATED,
  MUTE_CONVERSATION,
  MY_CONVERSATIONS,
} from "./queries";
import {
  canKeepSpelling,
  entryDisplayText,
  roleCanKeepSpelling,
  useVocabularyEntryUpdates,
} from "@/lib/vocabulary-entries";
import { claimCorrectionAnnouncement } from "@/lib/correction-announcements";
import { VocabularyEntryStateEnum } from "@/__generated__/graphql";
import { requestKeepSpelling } from "./keepSpelling";
import { MessageBubble } from "./MessageBubble";
import { MessageComposer } from "./MessageComposer";
import { TypingIndicator } from "./TypingIndicator";
import { SearchPanel } from "./SearchPanel";
import { ChatProvider } from "./ChatContext";
import { useConnectionState } from "./useConnectionState";
import type { ChatMessage, ChatUser, ChatViewer } from "./types";

const SCROLL_PIN_THRESHOLD_PX = 120;

interface Props {
  conversationId: string;
  currentUser: ChatViewer;
  otherParticipant: ChatUser | null;
  muted?: boolean;
  // The founder support thread: vocabulary capture there goes to the
  // student's Personal deck, never to the support account's tutor profile.
  isSupportThread?: boolean;
  // Conversation.tutorCanCapture, passed down to every bubble.
  tutorCanCapture?: boolean;
  onBack?: () => void;
}

// The header's role line was the raw role string ("tutor") capitalised —
// untranslated in sixteen locales.
const ROLE_KEYS: Record<string, string> = { student: "chat.role.student", tutor: "chat.role.tutor" };

export function ConversationView({
  conversationId,
  currentUser,
  otherParticipant,
  muted = false,
  isSupportThread = false,
  tutorCanCapture = false,
  onBack,
}: Props) {
  const { t, i18n } = useTranslation();
  const [reportOpen, setReportOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [pinnedToBottom, setPinnedToBottom] = useState(true);
  const [hasUnseenNew, setHasUnseenNew] = useState(false);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const connectionState = useConnectionState();
  const qc = useQueryClient();

  const { data, loading, refetch } = useQuery(CONVERSATION_MESSAGES, {
    variables: { conversationId, limit: 50 },
  });
  const [markRead] = useMutation(MARK_CONVERSATION_READ);
  const [muteConversation] = useMutation(MUTE_CONVERSATION, {
    refetchQueries: [{ query: MY_CONVERSATIONS }],
  });
  // A block or an unblock changes what the thread, the list and the badge show.
  const blockRefetches = [
    { query: MY_CONVERSATIONS },
    { query: CHAT_BLOCKED_USERS },
    { query: CONVERSATION_MESSAGES, variables: { conversationId, limit: 50 } },
  ];
  const [blockUser] = useMutation(BLOCK_USER, { refetchQueries: blockRefetches });
  const [unblockUser] = useMutation(UNBLOCK_USER, { refetchQueries: blockRefetches });
  const { data: blockedData } = useQuery(CHAT_BLOCKED_USERS);
  const isBlocked = !!otherParticipant && !!blockedData?.blockedUsers?.some((u) => u.id === otherParticipant.id);

  const onToggleMute = async () => {
    await muteConversation({ variables: { input: { conversationId, muted: !muted } } });
    toast.success(muted ? t("chat.toastUnmuted") : t("chat.toastMuted"));
  };

  const onBlock = async () => {
    if (!otherParticipant) return;
    if (!confirm(t("chat.blockConfirm", { name: otherParticipant.firstName }))) return;
    await blockUser({ variables: { input: { userId: otherParticipant.id } } });
    toast.success(t("chat.toastUserBlocked"));
  };

  const onUnblock = async () => {
    if (!otherParticipant) return;
    try {
      const { data: result } = await unblockUser({ variables: { input: { userId: otherParticipant.id } } });
      if (!result?.unblockUser?.success) throw new Error("unblock refused");
      toast.success(t("blockedUsersCard.unblockedToast", { name: otherParticipant.fullName ?? otherParticipant.firstName }));
    } catch {
      toast.error(t("blockedUsersCard.unblockFailedToast"));
    }
  };

  useSubscription(MESSAGE_ADDED, {
    variables: { conversationId },
    onData: () => {
      refetch();
      // If user is reading old messages (scrolled up), surface a
      // "↓ New message" floater instead of yanking them down.
      if (!pinnedToBottom) setHasUnseenNew(true);
    },
  });
  useSubscription(MESSAGE_UPDATED, {
    variables: { conversationId },
    onData: () => refetch(),
  });
  // A capture made from one of these messages changed state (queued →
  // creating → created / failed, or cancelled by an Undo): re-read the list so
  // the bubbles' saved-word underlines follow the server truth. Same
  // refetch-on-signal shape as the two message subscriptions above.
  //
  // One transition also deserves a word: a CORRECTED completion means the card
  // was created under a spelling nobody typed, and the popup could not say so
  // — it answers QUEUED and the AI runs a minute later. It carries the same
  // Keep-my-spelling action the classroom strip has.
  useVocabularyEntryUpdates({ conversationId }, (entry) => {
    refetch();
    // Only once the card exists: the correction is written before the picture
    // and audio legs, so a mid-flight (or failed) event carries it too, with
    // nothing yet to keep or to put back.
    if (entry.state !== VocabularyEntryStateEnum.Completed) return;
    // The shared rule for "is there a correction still worth reverting" —
    // skipCorrection is the server's "a revert is under way or done", and a
    // blank correction is no correction. One definition, or the surfaces drift.
    if (!canKeepSpelling(entry)) return;
    // Announced ONCE across surfaces, not just once per subscription event: a
    // capture made here inside a booked lesson also reaches the classroom's
    // notes pane, and two toasts would offer two competing actions.
    if (!claimCorrectionAnnouncement(entry.id)) return;
    const isTutor = currentUser.role === "tutor";
    // The shared bound on who may take the revert. This handler only ever fires
    // on a COMPLETED capture, so in practice the action is the student's alone:
    // the server grants a tutor the free path only and would answer their tap
    // not_found once the card exists. A tutor — like a viewer whose role has not
    // loaded — is told what happened rather than read as the student and handed
    // her first-person wording.
    const participant = roleCanKeepSpelling(currentUser.role, entry.state);
    // A conversation can have no other participant (a deleted account, a thread
    // still loading), and an empty name renders "Created for  as …". The file's
    // own translated fallback, the one the header and the report dialog use.
    const name = otherParticipant?.firstName || t("chat.defaultUser");
    // The spelling the card was made with, through the shared fallback.
    const corrected = entryDisplayText(entry.sourceText, entry.correctedText);
    toast.success(
      isTutor
        ? t("chat.vocabularyPopup.correctedToastTutor", { name, corrected, original: entry.sourceText })
        : t("chat.vocabularyPopup.correctedToast", { corrected, original: entry.sourceText }),
      participant
        ? {
            // First-person, with no role branch: the guard above runs only on a
            // COMPLETED capture, where roleCanKeepSpelling grants the action to
            // the student alone — a tutor's tap would be refused not_found.
            action: {
              label: t("chat.vocabularyPopup.keepSpelling"),
              onClick: () => {
                // Clicking the action dismisses the toast, so nothing is left
                // on screen to carry the restoring state — say it again.
                void requestKeepSpelling(entry.id, { t, qc }).then((accepted) => {
                  if (accepted) toast.info(t("chat.vocabularyPopup.restoringToast"));
                });
              },
            },
          }
        : undefined,
    );
  });

  useEffect(() => {
    if (!conversationId) return;
    // Only mark read when the user is actually viewing the latest messages.
    // Otherwise a subscription-driven refetch (data change) would clear the
    // unread count + send read receipts while they're scrolled up reading
    // older messages. Re-fires when they scroll back to the bottom.
    if (!pinnedToBottom) return;
    markRead({ variables: { input: { conversationId } } });
  }, [conversationId, data, pinnedToBottom, markRead]);

  // Track whether the user is "pinned" near the bottom. If they are,
  // auto-scroll on new messages. If not, leave them where they are
  // and show the new-message floater instead.
  const onScrollerScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const pinned = distanceFromBottom < SCROLL_PIN_THRESHOLD_PX;
    setPinnedToBottom(pinned);
    if (pinned) setHasUnseenNew(false);
  };

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    if (pinnedToBottom) {
      el.scrollTop = el.scrollHeight;
    }
  }, [data, pinnedToBottom]);

  // On conversation change, snap to bottom regardless
  useEffect(() => {
    setPinnedToBottom(true);
    setHasUnseenNew(false);
  }, [conversationId]);

  const scrollToBottom = (smooth = true) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
    setHasUnseenNew(false);
  };

  // Tap on a reply quote → scroll to original + flash
  const onJumpToMessage = useCallback((messageId: string) => {
    const target = document.getElementById(`message-${messageId}`);
    if (!target) return;
    target.scrollIntoView({ behavior: "smooth", block: "center" });
    target.classList.add("reply-flash");
    setTimeout(() => target.classList.remove("reply-flash"), 700);
  }, []);

  const messages = (data?.conversationMessages ?? []) as ChatMessage[];
  const otherName = otherParticipant
    ? otherParticipant.fullName ?? `${otherParticipant.firstName} ${otherParticipant.lastName}`
    : t("chat.defaultUser");
  const isOnline = otherParticipant?.online ?? false;

  return (
    <ChatProvider>
      <div className="relative flex h-full w-full bg-background">
        <section className="flex h-full min-w-0 flex-1 flex-col">
          {/* Header */}
          <header className="flex h-16 shrink-0 items-center gap-3 border-b border-border bg-background/95 px-4 backdrop-blur">
            {onBack && (
              <button
                onClick={onBack}
                className="md:hidden inline-flex size-9 items-center justify-center rounded-full hover:bg-secondary focus-ring"
                aria-label={t("chat.back")}
              >
                <ArrowLeft className="size-4" />
              </button>
            )}
            <div className="relative">
              <div className="flex size-9 items-center justify-center rounded-full bg-secondary text-[12px] font-semibold text-muted-foreground ring-1 ring-border">
                {otherParticipant?.initials ?? "?"}
              </div>
              {isOnline && (
                <span className="absolute bottom-0 right-0 size-2.5 rounded-full bg-success ring-2 ring-background" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[14px] font-medium leading-tight">{otherName}</div>
              <div className="text-[12px] text-muted-foreground">
                {isOnline && <span>{t("chat.activeNow")}</span>}
                {isOnline && otherParticipant?.role && <span> · </span>}
                {otherParticipant?.role && (
                  <span>{t(ROLE_KEYS[otherParticipant.role] ?? otherParticipant.role)}</span>
                )}
              </div>
            </div>
            <HeaderBtn label={t("chat.searchButton")} onClick={() => setSearchOpen((s) => !s)}>
              <Search className="size-4" />
            </HeaderBtn>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  aria-label={t("chat.more")}
                  className="inline-flex size-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-ring"
                >
                  <MoreHorizontal className="size-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onToggleMute}>
                  <BellOff className="mr-2 size-4" />
                  {muted ? t("chat.unmuteNotifications") : t("chat.muteNotifications")}
                </DropdownMenuItem>
                {otherParticipant && (
                  <DropdownMenuItem
                    onClick={isBlocked ? onUnblock : onBlock}
                    // Blocking is the destructive act; undoing it is not.
                    className={isBlocked ? undefined : "text-destructive focus:text-destructive"}
                  >
                    <Shield className="mr-2 size-4" />
                    {isBlocked
                      ? t("blockedUsersCard.unblockAria", { name: otherParticipant.firstName })
                      : t("chat.blockUser")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => setReportOpen(true)}
                  className="text-destructive focus:text-destructive"
                >
                  <Flag className="mr-2 size-4" />
                  {t("chat.reportUser")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </header>

          {/* Reconnecting banner — appears as a thin strip when the
              WebSocket drops. Disappears when reconnected. */}
          {connectionState === "disconnected" && (
            <div className="flex items-center justify-center gap-2 bg-warning/10 py-1.5 text-[12px] text-warning">
              <Loader2 className="size-3 animate-spin" />
              {t("chat.reconnecting")}
            </div>
          )}

          {/* Scroller */}
          <div
            ref={scrollerRef}
            onScroll={onScrollerScroll}
            className="scrollbar-thin relative flex-1 space-y-3 overflow-y-auto px-4 py-5 md:px-8"
          >
            {loading && !data ? (
              <div className="flex justify-center py-8">
                <Loader2 className="size-6 animate-spin text-muted-foreground" />
              </div>
            ) : messages.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                {t("chat.conversationEmpty")}
              </p>
            ) : (
              renderWithDateSeparators(messages, t, i18n.language, (msg) => (
                <MessageBubble
                  key={msg.id}
                  message={msg}
                  isOwn={msg.sender.id === currentUser.id}
                  conversationId={conversationId}
                  currentUserId={currentUser.id}
                  otherParticipant={otherParticipant}
                  isSupportThread={isSupportThread}
                  tutorCanCapture={tutorCanCapture}
                  onJumpToMessage={onJumpToMessage}
                />
              ))
            )}
          </div>

          {/* "↓ New message" floater — only when scrolled up */}
          {hasUnseenNew && (
            <div className="pointer-events-none flex justify-center">
              <button
                type="button"
                onClick={() => scrollToBottom(true)}
                className="pointer-events-auto -mt-12 mb-2 inline-flex items-center gap-1 rounded-full bg-foreground px-3 py-1.5 text-[12px] font-medium text-background shadow-lg hover:brightness-105 focus-ring"
              >
                <ArrowDown className="size-3.5" />
                {t("chat.newMessage")}
              </button>
            </div>
          )}

          {/* Typing indicator */}
          <TypingIndicator
            conversationId={conversationId}
            currentUserId={currentUser.id}
            otherName={otherParticipant?.firstName ?? t("chat.defaultUser")}
          />

          <MessageComposer conversationId={conversationId} />
        </section>

        {searchOpen && (
          <SearchPanel
            conversationId={conversationId}
            onClose={() => setSearchOpen(false)}
            onJumpToMessage={(id) => {
              setSearchOpen(false);
              setTimeout(() => onJumpToMessage(id), 100);
            }}
          />
        )}

        {otherParticipant && (
          <ReportUserDialog
            open={reportOpen}
            onOpenChange={setReportOpen}
            reportedUserId={otherParticipant.id}
            userName={otherName}
            context="messaging"
          />
        )}
      </div>
    </ChatProvider>
  );
}

function HeaderBtn({
  children,
  label,
  onClick,
}: {
  children: React.ReactNode;
  label: string;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="inline-flex size-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-ring"
    >
      {children}
    </button>
  );
}

/**
 * Inserts a "Today" separator before the first message of today.
 * Per spec we only label today; older messages get no separator.
 */
function renderWithDateSeparators(
  messages: ChatMessage[],
  t: (key: string) => string,
  locale: string,
  renderMessage: (msg: ChatMessage) => React.ReactNode,
) {
  const out: React.ReactNode[] = [];
  let lastDate: Date | null = null;
  for (const msg of messages) {
    const date = new Date(msg.createdAt);
    if (!lastDate || !isSameDay(lastDate, date)) {
      if (isToday(date)) {
        out.push(<DateSeparator key={`sep-${msg.id}`} label={t("chat.today")} />);
      } else if (isYesterday(date)) {
        out.push(<DateSeparator key={`sep-${msg.id}`} label={t("chat.yesterday")} />);
      } else {
        out.push(
          <DateSeparator key={`sep-${msg.id}`} label={formatShortDate(date, locale)} />,
        );
      }
    }
    out.push(renderMessage(msg));
    lastDate = date;
  }
  return out;
}

function DateSeparator({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-center py-2">
      <span
        className={cn(
          "rounded-full bg-secondary px-3 py-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground",
        )}
      >
        {label}
      </span>
    </div>
  );
}
